/**
 * showdownFx.coords — the PURE (Phaser-free, unit-tested) coordinate + timing
 * adapter for the Showdown animation port.
 *
 * Pokémon Showdown describes every effect in a small logical battle space:
 *   - x: − left,  + right
 *   - y: − down,  + up       (NOTE: +y is UP, the OPPOSITE of screen space)
 *   - z: − player (near), + opponent (far) — a fake-perspective depth that both
 *        shifts the anchor and shrinks the sprite.
 *   - scale / xscale / yscale / opacity / time
 *
 * Showdown's `BattleScene.pos()` (battle-animations.ts ~352-390) collapses that
 * logical location into a screen rect (left/top/width/height/opacity) using a
 * gen-5 perspective. We port that math verbatim so every ported move script
 * lands its effects exactly where Showdown intended, then map the result into
 * OUR scene via a single unit scale `U` and an origin offset `(originX, originY)`
 * (so the whole effect field can be parked over our battlers and resized).
 *
 * This module owns three concerns, all pure:
 *   1. {@link showdownPos}  — logical ScenePos → screen rect (centre x/y + display w/h + opacity).
 *   2. {@link inheritEnd}   — Showdown's `end = { ...start, ...end }` field inheritance
 *                             (incl. scale→xscale/yscale defaulting and the
 *                             end.time = start.time + 500 default).
 *   3. {@link EASING}       — Showdown transition name → Phaser ease name, plus
 *                             {@link isBallistic} for the parabolic transitions.
 *
 * No Phaser, no DOM, no IO — so it runs in the node vitest env.
 */

/** A Showdown logical scene location (mirrors PS `ScenePos`). All fields optional. */
export interface ScenePos {
  /** − left, + right */
  x?: number;
  /** − down, + up (inverted vs screen) */
  y?: number;
  /** − player (near), + opponent (far) */
  z?: number;
  scale?: number;
  xscale?: number;
  yscale?: number;
  opacity?: number;
  /** ms, relative to the anim's local clock (before timeOffset is added). */
  time?: number;
  display?: string;
}

/** A fully-defaulted Showdown location (every field present). */
export interface ResolvedScenePos {
  x: number;
  y: number;
  z: number;
  scale: number;
  xscale: number;
  yscale: number;
  opacity: number;
  time: number;
  display?: string;
}

/** The minimal sprite metadata `pos()` needs (matches a catalog entry). */
export interface FxSize {
  /** native width (px) */
  w: number;
  /** native height (px) */
  h: number;
  /** optional vertical anchor offset (PS `SpriteData.y`); defaults 0. */
  y?: number;
}

/**
 * A screen-space rectangle for a centred Phaser Image:
 *   - (x, y) is the CENTRE (Phaser images default to a 0.5/0.5 origin).
 *   - (w, h) is the display size (setDisplaySize).
 *   - opacity in [0, 1].
 *
 * Showdown's `pos()` returns a top-left rect; we convert to a centre here so the
 * caller can `img.setPosition(x, y)` / `img.setDisplaySize(w, h)` directly.
 */
export interface ScreenRect {
  x: number;
  y: number;
  w: number;
  h: number;
  opacity: number;
}

/**
 * How the logical Showdown field maps into our Phaser scene.
 *  - `U`            : unit scale (multiplies every Showdown px). 1 = native PS size.
 *  - `originX/Y`    : screen coords (px) of Showdown's reference point. Showdown's
 *                     `pos()` already bakes in its own `left=210, top=245` base; we
 *                     SUBTRACT that base and re-add `originX/originY` so the field
 *                     can be anchored anywhere in our scene.
 */
export interface FieldTransform {
  U: number;
  originX: number;
  originY: number;
}

/** Showdown's hard-coded screen reference baked into `pos()` (left/top base). */
export const PS_BASE_LEFT = 210;
export const PS_BASE_TOP = 245;

/**
 * Showdown's NEAR (player) and FAR (opponent) logical z. PS's `pos()` lays the two
 * combatants out along the z axis: the attacker sits NEAR (z=0, on the player base
 * at PS screen 210,245) and the defender sits FAR (z=200, on the opponent base at
 * PS screen 430,135 — see {@link psFieldPoint}). Giving the two Actors these
 * DISTINCT z values is what makes a script's `attacker -> defender` span travel the
 * full field instead of collapsing onto one shared point.
 */
export const NEAR_Z = 0;
export const FAR_Z = 200;

/**
 * The PS-screen offset (relative to the player base 210,245) of a logical point at
 * (x=0, y=0, z) — i.e. the `(left, top)` of {@link showdownPos} stripped of the
 * base and of any sprite-size term. Pure restatement of `pos()`'s z parallax:
 *   left += (410-190)*(z/200) ;  top += (135-245)*(z/200).
 * Near (z=0) → (0,0); far (z=200) → (220,-110). Used by {@link fitFieldToAnchors}.
 */
export function psFieldPoint(z: number): { px: number; py: number } {
  return { px: (410 - 190) * (z / 200), py: (135 - 245) * (z / 200) };
}

/**
 * Showdown transition name → Phaser ease string.
 *
 * Mapping (from battle-animations.ts posT ~396-449):
 *   - default / 'linear'          → 'Linear'
 *   - 'accel'                     → Quad.easeIn   (PS uses quadDown == ease-in)
 *   - 'decel'                     → Quad.easeOut  (PS uses quadUp   == ease-out)
 *   - 'swing'                     → Sine.easeInOut (jQuery default 'swing')
 *   - 'ballistic*' / 'quad*'      → handled specially (parabolic) — see isBallistic.
 *
 * jQuery naming is the inverse of the intuitive one: PS's `quadDown` accelerates
 * (ease-in) and `quadUp` decelerates (ease-out); `accel` uses quadDown, `decel`
 * uses quadUp — so accel→easeIn, decel→easeOut as above.
 */
export const EASING: Readonly<Record<string, string>> = {
  linear: 'Linear',
  accel: 'Quad.easeIn',
  decel: 'Quad.easeOut',
  swing: 'Sine.easeInOut',
  ballistic: 'Quad.easeOut',
  ballisticUnder: 'Quad.easeIn',
  ballistic2: 'Quad.easeOut',
  ballistic2Back: 'Quad.easeOut',
  ballistic2Under: 'Quad.easeIn',
};

/** The Showdown transitions whose vertical motion is parabolic (a jump arc). */
const BALLISTIC = new Set([
  'ballistic',
  'ballisticUnder',
  'ballistic2',
  'ballistic2Back',
  'ballistic2Under',
]);

/** True if `transition` is one of Showdown's parabolic (jumping) transitions. */
export function isBallistic(transition?: string): boolean {
  return !!transition && BALLISTIC.has(transition);
}

/**
 * Resolve a Phaser ease name for a transition (falls back to Linear). Ballistic
 * transitions don't have a single ease (their vertical channel is a parabola);
 * we return a sensible horizontal ease and let the caller apply the arc on Y.
 */
export function easeFor(transition?: string): string {
  if (!transition) return 'Linear';
  return EASING[transition] ?? 'Linear';
}

/**
 * Apply Showdown's `end = { ...start, ...end }` inheritance + defaults, exactly
 * as `animateEffect` does (battle-animations.ts ~290-297):
 *   - start.time defaults to 0.
 *   - end.time defaults to start.time + 500.
 *   - end.scale/xscale/yscale inherit from start when omitted (but an explicit 0
 *     is honoured — PS checks `!end.scale && end.scale !== 0`).
 *   - then end spreads over a copy of start, so any field omitted on end keeps
 *     its start value (position holds, fades stay put, etc.).
 *
 * Returns BOTH fully-resolved endpoints so the caller never re-derives defaults.
 */
export function inheritEnd(
  startIn: ScenePos,
  endIn: ScenePos,
): { start: ResolvedScenePos; end: ResolvedScenePos } {
  const start: ScenePos = { ...startIn };
  const end: ScenePos = { ...endIn };

  if (!start.time) start.time = 0;
  if (!end.time) end.time = start.time + 500;

  // scale → xscale/yscale inheritance from start (explicit 0 honoured).
  if (!end.scale && end.scale !== 0 && start.scale) end.scale = start.scale;
  if (!end.xscale && end.xscale !== 0 && start.xscale) end.xscale = start.xscale;
  if (!end.yscale && end.yscale !== 0 && start.yscale) end.yscale = start.yscale;

  // PS: end = { ...start, ...end } — undefined end fields fall back to start.
  const merged: ScenePos = { ...start };
  for (const k of Object.keys(end) as (keyof ScenePos)[]) {
    if (end[k] !== undefined) (merged[k] as number | string) = end[k]!;
  }

  return { start: resolve(start), end: resolve(merged) };
}

/** Fill every optional field of a ScenePos with its Showdown default. */
export function resolve(loc: ScenePos): ResolvedScenePos {
  const scale = loc.scale ?? 1;
  const r: ResolvedScenePos = {
    x: loc.x ?? 0,
    y: loc.y ?? 0,
    z: loc.z ?? 0,
    scale,
    // PS: `if (!loc.xscale && loc.xscale !== 0) loc.xscale = loc.scale;`
    xscale: loc.xscale ?? (loc.xscale === 0 ? 0 : scale),
    yscale: loc.yscale ?? (loc.yscale === 0 ? 0 : scale),
    opacity: loc.opacity ?? 1,
    time: loc.time ?? 0,
  };
  if (loc.display !== undefined) r.display = loc.display;
  return r;
}

/**
 * Port of Showdown's `BattleScene.pos(loc, obj)` (battle-animations.ts ~352-390),
 * adapted to return a CENTRE-anchored screen rect in OUR scene space.
 *
 * Faithful to PS:
 *   - gen-5 perspective scale: `scale = 2.0 - z/200` (clamped ≥ 0.1).
 *   - z parallax shift of the anchor (the `(410-190)` / `(135-245)` terms).
 *   - x scaled & added to left; y scaled & SUBTRACTED from top (the +y-is-up flip).
 *   - width/height = obj.{w,h} * scale * {x,y}scale.
 *   - the centring offsets (PS subtracts width/2 and hoffset/2 from the top-left;
 *     since we return a centre, we instead apply the `hoffset` vertical anchor
 *     correction directly).
 *
 * Then mapped into our scene: subtract PS's base, multiply by `U`, add origin.
 */
export function showdownPos(loc: ResolvedScenePos, obj: FxSize, field: FieldTransform): ScreenRect {
  // gen-5 perspective scale.
  let scale = 2.0 - loc.z / 200;
  if (scale < 0.1) scale = 0.1;

  // PS base + z parallax.
  let left = PS_BASE_LEFT;
  let top = PS_BASE_TOP;
  left += (410 - 190) * (loc.z / 200);
  top += (135 - 245) * (loc.z / 200);

  // x → right, y → up (subtracted), both perspective-scaled. PS floors these.
  left += Math.floor(loc.x * scale);
  top -= Math.floor(loc.y * scale);

  const width = Math.floor(obj.w * scale * loc.xscale);
  const height = Math.floor(obj.h * scale * loc.yscale);

  // PS top-left placement subtracts width/2 (horizontal centre) and hoffset/2
  // (a vertical anchor that accounts for the sprite's foot offset obj.y).
  const hoffset = Math.floor((obj.h - (obj.y ?? 0) * 2) * scale * loc.yscale);

  // PS top-left:  leftTL = left - width/2 ; topTL = top - hoffset/2.
  // Our centre:   centreX = leftTL + width/2  = left
  //               centreY = topTL  + height/2 = top - hoffset/2 + height/2.
  const psCentreX = left;
  const psCentreY = top - Math.floor(hoffset / 2) + Math.floor(height / 2);

  // Map PS screen space → our scene space.
  const { U, originX, originY } = field;
  return {
    x: originX + (psCentreX - PS_BASE_LEFT) * U,
    y: originY + (psCentreY - PS_BASE_TOP) * U,
    w: width * U,
    h: height * U,
    opacity: loc.opacity,
  };
}

/** One end of the 2-point field fit: a real screen anchor + its logical z. */
export interface FieldFitAnchor {
  /** screen x (px) the combatant body sits at */
  x: number;
  /** screen y (px) the combatant body sits at */
  y: number;
  /** the combatant's logical Showdown z (NEAR_Z for near, FAR_Z for far) */
  z: number;
}

/**
 * Fit a {@link FieldTransform} so the NEAR combatant's logical anchor lands on its
 * real screen point and the FAR combatant's logical anchor lands on (or as near as
 * an isotropic scale + translation can reach along the near→far axis) its real
 * screen point. THIS is the fix for the "beam collapses to a point" bug: the two
 * combatants used to share one logical position (0,0,0) at the field midpoint, so
 * any `attacker -> defender` span had zero length. Now each carries its own z
 * ({@link NEAR_Z} / {@link FAR_Z}); fitting their two logical points onto the two
 * real anchors makes a beam STRETCH the full path from caster to target.
 *
 * The fit (isotropic U + translation, matching {@link showdownPos}'s mapping):
 *   - Each logical point maps to PS-screen offset `psFieldPoint(z)` (relative to the
 *     player base). The vector between the two is `dP = psFieldPoint(far) -
 *     psFieldPoint(near)` (PS-native near→far axis, ≈ (220,-110) for z 0→200).
 *   - The real vector between the two screen anchors is `dA = far - near`.
 *   - `U` = the scalar that best maps `dP` onto `dA` along that axis (projection /
 *     least-squares, so an aspect-ratio mismatch can't blow up): `U = (dA·dP)/(dP·dP)`.
 *   - `origin` is then chosen so the MIDPOINT of the two logical anchors lands on the
 *     midpoint of the two real anchors. This balances the residual evenly between the
 *     two ends (rather than pinning one end exactly and letting the other drift), so
 *     both the caster and the target sit on their sprites and the beam reads true.
 *
 * Degenerate inputs (coincident anchors, or equal z so `dP` is zero) fall back to a
 * midpoint origin at U=1 — exactly the old behaviour, so nothing regresses.
 */
export function fitFieldToAnchors(near: FieldFitAnchor, far: FieldFitAnchor): FieldTransform {
  const pNear = psFieldPoint(near.z);
  const pFar = psFieldPoint(far.z);
  const dPx = pFar.px - pNear.px;
  const dPy = pFar.py - pNear.py;
  const dPlen2 = dPx * dPx + dPy * dPy;

  const midAx = (near.x + far.x) / 2;
  const midAy = (near.y + far.y) / 2;
  const midPx = (pNear.px + pFar.px) / 2;
  const midPy = (pNear.py + pFar.py) / 2;

  if (dPlen2 < 1e-9) {
    // No logical separation (same z) — nothing to span; keep the old midpoint anchor.
    return { U: 1, originX: midAx - midPx, originY: midAy - midPy };
  }

  const dAx = far.x - near.x;
  const dAy = far.y - near.y;
  // Project the real axis onto the PS axis: the U that best matches their lengths.
  const U = (dAx * dPx + dAy * dPy) / dPlen2;

  // Anchor so the two logical midpoints coincide on screen (residual split evenly).
  // showdownPos maps a PS offset p to: screen = origin + (p - base)*U + base*U? No —
  // it maps (psCentre - base)*U + origin, and psFieldPoint already IS (psCentre-base)
  // for a zero-size point, so screen = origin + p*U. Hence origin = midA - midP*U.
  const originX = midAx - midPx * U;
  const originY = midAy - midPy * U;
  return { U, originX, originY };
}

/**
 * Per-side facing helpers, mirroring Showdown's `Sprite` methods
 * (battle-animations.ts ~1985-1996). `isFrontSprite` = the opponent (far) side;
 * the player (near) side flips every sign so "behind / left of" reads correctly
 * for whichever way the combatant faces.
 *
 * These return LOGICAL Showdown coordinates (the ported scripts then feed them
 * back through showdownPos), so they live here in the pure layer.
 */
export interface ActorAnchor {
  x: number;
  y: number;
  z: number;
  isFrontSprite: boolean;
}

/** PS `behindx(offset)` — x shifted "behind", sign per facing. */
export function behindx(a: ActorAnchor, offset: number): number {
  return a.x + (a.isFrontSprite ? 1 : -1) * offset;
}
/** PS `behindy(offset)` — y shifted "behind" (note the inverted sign). */
export function behindy(a: ActorAnchor, offset: number): number {
  return a.y + (a.isFrontSprite ? -1 : 1) * offset;
}
/** PS `leftof(offset)` — same sign convention as behindx. */
export function leftof(a: ActorAnchor, offset: number): number {
  return a.x + (a.isFrontSprite ? 1 : -1) * offset;
}
/** PS `behind(offset)` — z shifted "behind", sign per facing. */
export function behind(a: ActorAnchor, offset: number): number {
  return a.z + (a.isFrontSprite ? 1 : -1) * offset;
}

/**
 * Sample a value along a 0..1 progress with a Showdown easing. Pure, so the test
 * suite (and any non-Phaser caller) can reason about the curves. Mirrors Phaser's
 * named eases closely enough for verification:
 *   - Linear, Quad.easeIn (t²), Quad.easeOut (1-(1-t)²), Sine.easeInOut.
 */
export function ease(name: string, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  switch (name) {
    case 'Quad.easeIn':
      return k * k;
    case 'Quad.easeOut':
      return 1 - (1 - k) * (1 - k);
    case 'Sine.easeInOut':
      return -(Math.cos(Math.PI * k) - 1) / 2;
    case 'Linear':
    default:
      return k;
  }
}

/**
 * A parabolic vertical offset for the ballistic (jumping) transitions, peaking
 * mid-flight and returning to 0 at both ends: `4 * h * t * (1 - t)`. `t` is 0..1
 * progress; the caller adds this (in screen px, sign per up/down) to the linearly
 * interpolated Y. Approximates jQuery's ballisticUp/Down arc.
 */
export function ballisticArc(t: number, height: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return 4 * height * k * (1 - k);
}
